import React, { useState } from "react";
import axios, { AxiosError } from "axios";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import AuthModal from '../../Components/Modal/AuthenticateModal';
import { toast } from 'sonner';

export default function Settings() {
    const [currentPassword, setCurrentPassword] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [isUpdating, setIsUpdating] = useState(false);
    const [error, setError] = useState<any | null>(null);

    const [showCurrent, setShowCurrent] = useState(false);
    const [showNew, setShowNew] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);

    const [isModalOpen, setIsModalOpen] = useState(false); 
    const [isLoading, setIsLoading] = useState(false);

    const openAuthModal = () => {
		setIsModalOpen(true); 
	};
	
	const closeAuthModal = () => {
		setIsModalOpen(false); 
	};

    const navigate = useNavigate();
    const token = localStorage.getItem("authToken");
    
    const handleUpdatePassword = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsUpdating(true);
        setError(null);

        if (!currentPassword) {
            toast.error(<p className="text-[16px]">Current password is required.</p>);
            setIsUpdating(false);
            return;
        }

        if (newPassword.length < 8) {
            toast.error(<p className="text-[16px]">Password must be at least 8 characters.</p>);
            setIsUpdating(false);
            return;
        }

        if (newPassword !== confirmPassword) {
            toast.error(<p className="text-[16px]">Passwords do not match.</p>);
            setIsUpdating(false);
            return;
        }

        try {
            const response = await axios.post("https://cdn.pulp.ph/api/v1/pulpmagazine/updatePassword",
                {
                    currentPassword,
                    newPassword,
                    confirmPassword
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            const { status, message } = response.data;

            if (status === "success") {
                setIsModalOpen(false);
                toast.success(<p className="text-[16px]">{message}</p>, { 
                    duration: 1200 
                });

                localStorage.removeItem("authToken");
                setTimeout(() => {
                    navigate("/login");
                }, 1300);
            }
        } catch (error: unknown) {
            let msg = "Something went wrong.";

            if (axios.isAxiosError(error) && error.response) {
                if ((error.response.data as any)?.error) {
                    msg = (error.response.data as any).error;
                }
                else if ((error.response.data as any)?.errors) {
                    Object.values((error.response.data as any).errors).forEach((val: any) => {
                        toast.error(<p className="text-[16px]">{val}</p>);
                    });
                    setIsUpdating(false);
                    return;
                }
            }

            toast.error(<p className="text-[16px]">{msg}</p>);
        } finally {
            setIsUpdating(false);
        }
    };

    return (
        <>
            <AuthModal
                isOpen={isModalOpen}
                onClose={closeAuthModal}
                title="Action Verification"
                content={<div className='text-base mb-3'>Do you want to update your password?</div>}
                footer={
                    <>
                        <button 
                            className="m-0 px-4 py-2 GraphikSemibold text-sm text-gray-700 bg-gray-200 rounded hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-400"
                            onClick={closeAuthModal}>
                            Close
                        </button>

                        <button 
							disabled={isLoading}
							className="m-0 px-4 py-2 GraphikSemibold text-sm text-white bg-red-500 rounded hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-400"
                            onClick={handleUpdatePassword}>
							{isLoading ? "Confirming..." : "Confirm"}
                        </button>
                    </>
                }
            />

            <div className="w-full p-8">
                <h1 className="GraphikSemibold text-[20px] mb-6">Settings</h1>

                <div className="bg-white shadow-sm rounded-lg p-6 mb-6">
                    <h2 className="GraphikSemibold text-lg mb-4">Profile Information</h2>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <input
                        type="text"
                        placeholder="Full Name"
                        className="border p-2 rounded w-full"
                    />

                    <input
                        type="email"
                        placeholder="Email Address"
                        className="border p-2 rounded w-full"
                    />
                    </div>

                    <button className="mt-4 px-4 py-2 bg-black text-white rounded hover:bg-gray-800">
                        Save Changes
                    </button>
                </div>

                <div className="bg-white shadow-sm rounded-lg p-6">
                    <h2 className="GraphikSemibold text-lg mb-4">Change Password</h2>

                    <div className="space-y-4">
                        <div className="relative w-full">
                            <input
                                type={showCurrent ? "text" : "password"}
                                id="currentPassword"
                                className="w-full text-sm tracking-[1px] p-2 border rounded"
                                onChange={(e) => setCurrentPassword(e.target.value)}
                                placeholder="Current Password"
                            />
                            <span
                                className="absolute right-2 top-1/2 -translate-y-1/2 cursor-pointer text-gray-500"
                                onClick={() => setShowCurrent(!showCurrent)}
                            >
                                <FontAwesomeIcon icon={showCurrent ? faEyeSlash : faEye} />
                            </span>
                        </div>
                    
                        <div className="relative w-full">
                            <input
                                type={showNew ? "text" : "password"}
                                id="newPassword"
                                className="w-full text-sm tracking-[1px] p-2 border rounded"
                                onChange={(e) => setNewPassword(e.target.value)}
                                placeholder="New Password"
                            />
                            <span
                                className="absolute right-2 top-1/2 -translate-y-1/2 cursor-pointer text-gray-500"
                                onClick={() => setShowNew(!showNew)}
                            >
                                <FontAwesomeIcon icon={showNew ? faEyeSlash : faEye} />
                            </span>
                        </div>

                        <div className="relative w-full">
                            <input
                                type={showConfirm ? "text" : "password"}
                                id="confirmPassword"
                                className="w-full text-sm tracking-[1px] p-2 border rounded"
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                placeholder="Confirm New Password"
                            />
                            <span
                                className="absolute right-2 top-1/2 -translate-y-1/2 cursor-pointer text-gray-500"
                                onClick={() => setShowConfirm(!showConfirm)}
                            >
                                <FontAwesomeIcon icon={showConfirm ? faEyeSlash : faEye} />
                            </span>
                        </div>
                    </div>

                    <button
                        type="button"
                        onClick={openAuthModal}
                        disabled={isUpdating || isModalOpen}
                        className="GraphikSemibold mt-4 px-4 py-2 text-sm tracking-[1px] text-white bg-red-600 hover:bg-red-800 active:translate-y-[1px] rounded-md disabled:opacity-60 disabled:cursor-not-allowed transition"
                    >
                        {isUpdating ? "Updating..." : "Update Password"}
                    </button>
                </div>
            </div>
        </>
    );
}